/**
 * The spin control (UI-001, inline since UI-009): a cue ball you place the tip on.
 *
 * UI-009 (owner, 2026-09-25): no Spin button to open it. The ball sits beside Shoot all
 * the time and is used directly. It also used to have a text readout beside it
 * ("0.2R draw · 0.5R right") whose length changed as the tip moved, resizing the box
 * so it looked like it glitched. There is no visible text now, so nothing can move:
 * the words live in the ball's accessible label and tooltip instead.
 *
 * Tap: the tip goes where you tapped; a tap on the middle is centre ball. Drag: the tip
 * moves from where it was at half scale, so the finger never hides the spot it is
 * setting (game/spin.ts, the same idea as aiming, UI-008).
 *
 * Centre ball until touched, because most shots want no spin. The ball's face is R
 * across its radius; the dashed ring is `maxTipOffsetR`, past which a real cue
 * miscues and `validateShot` refuses the shot.
 *
 * UI-010 (owner, 2026-09-26): a small button on the ball's corner puts the tip back in
 * the middle in one press. It is there only while there is spin to take off, and it
 * sits over the corner rather than beside the ball, so appearing moves nothing.
 */
import {
  CENTRE_BALL,
  SPIN_VIEW_R,
  describeTipOffset,
  pixelsPerR,
  dragOffset,
  isCentreBall,
  tapOffset,
  clampTipOffset,
  type TipOffset,
} from '../game/spin.ts';
import { TAP_SLOP_PX } from '../game/aim-gesture.ts';
import { SPIN_COPY } from '../config/site.ts';

/** A curved arrow back to the middle. Markup only, so the CSP needs nothing. */
const RESET_ICON = `<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
  <path d="M3.5 8a4.5 4.5 0 1 0 1.4-3.3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
  <path d="M3 2.2v3.3h3.3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

export interface SpinPad {
  value(): TipOffset;
  reset(): void;
  setEnabled(enabled: boolean): void;
  destroy(): void;
}

/** One arrow-key press, in units of R. Fine enough to place, coarse enough to use. */
const KEY_STEP_R = 0.05;

export function createSpinPad(
  host: HTMLElement,
  maxOffsetR: number,
  onChange: (offset: TipOffset) => void,
): SpinPad {
  host.innerHTML = `
    <div class="spin__pad" data-pad tabindex="0" role="application">
      <span class="spin__limit" data-limit></span>
      <span class="spin__tip" data-tip></span>
    </div>
    <button type="button" class="spin__reset" data-reset hidden
            aria-label="${SPIN_COPY.reset}" title="${SPIN_COPY.reset}">${RESET_ICON}</button>
  `;

  const pad = need<HTMLDivElement>(host, '[data-pad]');
  const limit = need<HTMLSpanElement>(host, '[data-limit]');
  const tip = need<HTMLSpanElement>(host, '[data-tip]');
  const resetButton = need<HTMLButtonElement>(host, '[data-reset]');

  // The ring is the cap as a fraction of what is drawn (UI-014: the middle of the ball,
  // magnified), so it tracks the cap if the cap is tuned.
  limit.style.setProperty('--spin-limit', String(maxOffsetR / SPIN_VIEW_R));

  let offset: TipOffset = CENTRE_BALL;

  function render(): void {
    // +y is follow, which is UP the ball face and so up the screen: negate for CSS.
    tip.style.setProperty('--tip-x', String(offset.spinX / SPIN_VIEW_R));
    tip.style.setProperty('--tip-y', String(-offset.spinY / SPIN_VIEW_R));
    const words = describeTipOffset(offset);
    pad.setAttribute(
      'aria-label',
      `Spin: ${words}. Tap to place, drag to adjust, tap the middle for centre ball.`,
    );
    pad.title = `Spin: ${words}`;
    const centred = isCentreBall(offset);
    pad.classList.toggle('spin__pad--set', !centred);
    // Keyboard focus is not lost with the button: it goes back to the ball.
    if (centred && document.activeElement === resetButton) pad.focus();
    resetButton.hidden = centred;
  }

  function set(next: TipOffset): void {
    if (next.spinX === offset.spinX && next.spinY === offset.spinY) return;
    offset = next;
    render();
    onChange(offset);
  }

  /** The press in progress: where it began, and the tip at that moment. */
  let press: { x: number; y: number; from: TipOffset; dragging: boolean } | undefined;

  const radius = (): number => pixelsPerR(pad.getBoundingClientRect().width / 2);

  const onPointerDown = (event: PointerEvent): void => {
    if (pad.hasAttribute('aria-disabled') || event.button !== 0) return;
    event.preventDefault();
    pad.setPointerCapture(event.pointerId);
    press = { x: event.clientX, y: event.clientY, from: offset, dragging: false };
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (press === undefined) return;
    const dx = event.clientX - press.x;
    const dy = event.clientY - press.y;
    if (!press.dragging && dx * dx + dy * dy < TAP_SLOP_PX * TAP_SLOP_PX) return;
    press.dragging = true;
    set(dragOffset(press.from, dx, dy, radius(), maxOffsetR));
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (press !== undefined && !press.dragging) {
      const rect = pad.getBoundingClientRect();
      set(
        tapOffset(
          press.x - (rect.left + rect.width / 2),
          press.y - (rect.top + rect.height / 2),
          pixelsPerR(rect.width / 2),
          maxOffsetR,
        ),
      );
    }
    press = undefined;
    if (pad.hasPointerCapture(event.pointerId)) pad.releasePointerCapture(event.pointerId);
  };

  const onPointerCancel = (event: PointerEvent): void => {
    // A cancelled touch changes nothing it had not already changed.
    press = undefined;
    if (pad.hasPointerCapture(event.pointerId)) pad.releasePointerCapture(event.pointerId);
  };

  const KEYS: Record<string, readonly [number, number]> = {
    ArrowLeft: [-KEY_STEP_R, 0],
    ArrowRight: [KEY_STEP_R, 0],
    ArrowUp: [0, KEY_STEP_R],
    ArrowDown: [0, -KEY_STEP_R],
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (pad.hasAttribute('aria-disabled')) return;
    if (event.key === 'Home' || event.key === 'Escape' || event.key === '0') {
      event.preventDefault();
      set(CENTRE_BALL);
      return;
    }
    const step = KEYS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    set(clampTipOffset(offset.spinX + step[0], offset.spinY + step[1], maxOffsetR));
  };

  pad.addEventListener('pointerdown', onPointerDown);
  pad.addEventListener('pointermove', onPointerMove);
  pad.addEventListener('pointerup', onPointerUp);
  pad.addEventListener('pointercancel', onPointerCancel);
  pad.addEventListener('keydown', onKeyDown);

  const onReset = (): void => {
    if (pad.hasAttribute('aria-disabled')) return;
    set(CENTRE_BALL);
  };
  resetButton.addEventListener('click', onReset);

  render();

  return {
    value: () => offset,
    reset: () => {
      set(CENTRE_BALL);
    },
    setEnabled(enabled) {
      resetButton.disabled = !enabled;
      if (enabled) pad.removeAttribute('aria-disabled');
      else {
        pad.setAttribute('aria-disabled', 'true');
        press = undefined;
      }
    },
    destroy() {
      pad.removeEventListener('pointerdown', onPointerDown);
      pad.removeEventListener('pointermove', onPointerMove);
      pad.removeEventListener('pointerup', onPointerUp);
      pad.removeEventListener('pointercancel', onPointerCancel);
      pad.removeEventListener('keydown', onKeyDown);
      resetButton.removeEventListener('click', onReset);
    },
  };
}

function need<T extends Element>(root: ParentNode, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (found === null) throw new Error(`spin pad: ${selector} is missing`);
  return found;
}
